import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './auth/AuthContext.jsx';
import { ProtectedRoute } from './auth/ProtectedRoute.jsx';
import { Layout } from './components/Layout.jsx';
import { LoginPage } from './pages/LoginPage.jsx';
import { UnderwriterDashboard } from './pages/UnderwriterDashboard.jsx';
import { UnderwritingCanvas } from './pages/UnderwritingCanvas.jsx';
import { SubmissionDetail } from './pages/SubmissionDetail.jsx';
import { AccountPage } from './pages/AccountPage.jsx';
import { LeadershipDashboard } from './pages/LeadershipDashboard.jsx';
import { BrokerPortal } from './pages/BrokerPortal.jsx';
import { BrokerQuoteView } from './pages/BrokerQuoteView.jsx';
import { ProductManagement } from './pages/ProductManagement.jsx';
import { ProductDetail } from './pages/ProductDetail.jsx';
import { CapacityPartners } from './pages/CapacityPartners.jsx';
import { BrokerDashboard } from './pages/BrokerDashboard.jsx';
import { BrokerDetail } from './pages/BrokerDetail.jsx';
import { Documentation } from './pages/Documentation.jsx';
import { PolicyManagement } from './pages/PolicyManagement.jsx';

function App() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <p>Loading...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Layout>
      <Routes>
        {/* ── Underwriting Hub (/) ── */}
        <Route
          path="/"
          element={
            <ProtectedRoute groups={['underwriter', 'leadership']}>
              <UnderwriterDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/submissions/:id"
          element={
            <ProtectedRoute groups={['underwriter', 'leadership']}>
              <SubmissionDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/underwriting/:submissionId"
          element={
            <ProtectedRoute groups={['underwriter', 'leadership']}>
              <UnderwritingCanvas />
            </ProtectedRoute>
          }
        />
        <Route
          path="/accounts/:id"
          element={
            <ProtectedRoute groups={['underwriter', 'leadership']}>
              <AccountPage />
            </ProtectedRoute>
          }
        />

        {/* ── Distribution Hub (/distribution) ── */}
        <Route
          path="/distribution"
          element={
            <ProtectedRoute groups={['underwriter', 'leadership']}>
              <BrokerDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/brokers/:id"
          element={
            <ProtectedRoute groups={['underwriter', 'leadership']}>
              <BrokerDetail />
            </ProtectedRoute>
          }
        />

        {/* ── Products Hub (/products) ── */}
        <Route
          path="/products"
          element={
            <ProtectedRoute groups={['underwriter', 'leadership']}>
              <ProductManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/products/:id"
          element={
            <ProtectedRoute groups={['underwriter', 'leadership']}>
              <ProductDetail />
            </ProtectedRoute>
          }
        />

        {/* ── PAS Hub (/pas) ── */}
        <Route
          path="/pas"
          element={
            <ProtectedRoute groups={['underwriter', 'leadership']}>
              <PolicyManagement />
            </ProtectedRoute>
          }
        />

        {/* ── Leadership Hub (/leadership) ── */}
        <Route
          path="/leadership"
          element={
            <ProtectedRoute groups={['leadership']}>
              <LeadershipDashboard />
            </ProtectedRoute>
          }
        />

        {/* Capacity Partners (standalone for capacity-provider group) */}
        <Route
          path="/capacity-partners"
          element={
            <ProtectedRoute groups={['leadership', 'capacity-provider']}>
              <CapacityPartners />
            </ProtectedRoute>
          }
        />

        {/* ── Resources Hub (/resources) ── */}
        <Route
          path="/resources"
          element={
            <ProtectedRoute groups={['underwriter', 'leadership']}>
              <Documentation />
            </ProtectedRoute>
          }
        />

        {/* ── Broker Portal ── */}
        <Route
          path="/broker"
          element={
            <ProtectedRoute groups={['broker']}>
              <BrokerPortal />
            </ProtectedRoute>
          }
        />
        <Route
          path="/broker/quotes/:id"
          element={
            <ProtectedRoute groups={['broker']}>
              <BrokerQuoteView />
            </ProtectedRoute>
          }
        />

        {/* ── Legacy route redirects ── */}
        <Route path="/accounts" element={<Navigate to="/" replace />} />
        <Route path="/reviews" element={<Navigate to="/" replace />} />
        <Route path="/brokers" element={<Navigate to="/distribution" replace />} />
        <Route path="/approvals" element={<Navigate to="/products" replace />} />
        <Route path="/claims" element={<Navigate to="/products" replace />} />
        <Route path="/policies" element={<Navigate to="/pas" replace />} />
        <Route path="/commissions" element={<Navigate to="/pas" replace />} />
        <Route path="/compliance" element={<Navigate to="/pas" replace />} />
        <Route path="/treaties" element={<Navigate to="/pas" replace />} />
        <Route path="/audit-trail" element={<Navigate to="/leadership" replace />} />
        <Route path="/docs" element={<Navigate to="/resources" replace />} />
        <Route path="/api-docs" element={<Navigate to="/resources" replace />} />

        {/* Fallback */}
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

export default App;
